import { useEffect } from "react";
import confetti from "canvas-confetti";
import "./OfferUnlockedModal.css";

/**
 * OfferUnlockedModal
 *
 * Fully self-contained — no image assets required.
 * Hero, chips, terms, and CTA are all rendered in code.
 *
 * Props:
 *   isOpen         {boolean}
 *   offerData      {object}  — { mealsPerValidity, offerCap, validity,
 *                                validityExcludesWeekends }
 *                              `validity` is the configured per-customer
 *                              window, NOT days remaining. Both come straight
 *                              from /api/offer/acquisition/status.
 *   onClose        {function}
 *   onStartMeal    {function}  — called when CTA is tapped (optional)
 */
const OfferUnlockedModal = ({ isOpen, offerData, onClose, onStartMeal }) => {
  // Fire confetti burst 300ms after the modal opens
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.5 },
        colors: ["#a8e63d", "#f5c842", "#ffffff", "#2d7a2d", "#ff6b6b"],
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  if (!isOpen || !offerData) return null;

  const {
    mealsPerValidity = 5,
    offerCap = 79,
    lockedPrice = 89,
    validity = 7,
    validityExcludesWeekends = false,
  } = offerData;


  return (
    <div className="oum-overlay" onClick={onClose}>
      <div
        className="oum-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Offer unlocked"
      >
        {/* ── Close ── */}
        <button className="oum-close-btn" onClick={onClose} aria-label="Close">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* ── HERO ── */}
        <div className="oum-hero">
          <div className="oum-hero-blob oum-hero-blob--tl" />
          <div className="oum-hero-blob oum-hero-blob--br" />

          {/* Gift icon ring */}
          <div className="oum-gift-ring" aria-hidden="true">
            <span className="oum-gift-emoji">🎁</span>
          </div>

          {/* Headline */}
          <p className="oum-headline-top">You've unlocked</p>
          <p className="oum-headline-main">
            <span className="oum-headline-count">{mealsPerValidity}</span>
            <span className="oum-headline-word"> free meals.</span>
          </p>

          {/* Sub-copy pills */}
          <div className="oum-hero-sub">
            <span className="oum-sub-item">One per day</span>
            <span className="oum-sub-dot" aria-hidden="true" />
            <span className="oum-sub-item">Up to ₹{offerCap} each</span>
            <span className="oum-sub-dot" aria-hidden="true" />
            <span className="oum-sub-item">
              Valid for {validity}{" "}
              {validityExcludesWeekends ? "working days" : "days"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferUnlockedModal;